import React, { useState, useEffect } from 'react';
import RatingStars from './RatingStars';
import ReviewItem from './ReviewItem';
import ReviewForm from './ReviewForm';
import { useToast } from './ui';
import './ProductReviews.css';

/**
 * ProductReviews Component
 * Main component for displaying and managing product reviews
 *
 * Props:
 * - productId: number
 * - user: object (current user, null if not logged in)
 */
const ProductReviews = ({ productId, user }) => {
  const toast = useToast();
  const [reviews, setReviews] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReviews();
  }, [productId, sortBy]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/reviews/product/${productId}?sort=${sortBy}`);

      if (!response.ok) {
        throw new Error('Failed to fetch reviews');
      }

      const data = await response.json();
      setReviews(data.reviews);
      setStatistics(data.statistics);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (reviewData) => {
    if (!user) {
      throw new Error('Please log in to submit a review');
    }

    const response = await fetch('/api/reviews', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.token}`
      },
      body: JSON.stringify({
        ...reviewData,
        user_id: user.id
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to submit review');
    }

    setShowReviewForm(false);
    toast.success('Review submitted — pending admin approval.');
  };

  const handleVote = async (reviewId, isHelpful) => {
    if (!user) {
      toast.warn('Please log in to vote on reviews');
      return;
    }

    const response = await fetch(`/api/reviews/${reviewId}/vote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.token}`
      },
      body: JSON.stringify({
        user_id: user.id,
        is_helpful: isHelpful
      })
    });

    if (!response.ok) {
      throw new Error('Failed to record vote');
    }
  };

  const getRatingDistributionPercentage = (count) => {
    if (!statistics || statistics.total_reviews === 0) return 0;
    return (count / statistics.total_reviews) * 100;
  };

  if (loading && reviews.length === 0) {
    return (
      <div className="product-reviews">
        <div className="loading-message">Loading reviews...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-reviews">
        <div className="error-message">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="product-reviews">
      <h2 className="reviews-heading">Customer Reviews</h2>

      {statistics && (
        <div className="reviews-summary">
          <div className="summary-rating">
            <div className="average-rating">
              {statistics.average_rating.toFixed(1)}
            </div>
            <RatingStars rating={statistics.average_rating} size="large" />
            <div className="total-reviews">
              Based on {statistics.total_reviews} review{statistics.total_reviews !== 1 ? 's' : ''}
            </div>
          </div>

          <div className="rating-distribution">
            {[5, 4, 3, 2, 1].map(stars => (
              <div key={stars} className="distribution-row">
                <span className="stars-label">{stars} ★</span>
                <div className="distribution-bar">
                  <div
                    className="distribution-fill"
                    style={{
                      width: `${getRatingDistributionPercentage(
                        statistics.rating_distribution[stars]
                      )}%`
                    }}
                  />
                </div>
                <span className="distribution-count">
                  {statistics.rating_distribution[stars]}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="reviews-controls">
        <div className="sort-controls">
          <label htmlFor="sort-select">Sort by:</label>
          <select
            id="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="recent">Most Recent</option>
            <option value="helpful">Most Helpful</option>
            <option value="rating_high">Highest Rating</option>
            <option value="rating_low">Lowest Rating</option>
          </select>
        </div>

        {user && !showReviewForm && (
          <button
            className="write-review-button"
            onClick={() => setShowReviewForm(true)}
          >
            Write a Review
          </button>
        )}

        {!user && (
          <p className="login-prompt">
            <a href="/login">Log in</a> to write a review
          </p>
        )}
      </div>

      {showReviewForm && (
        <ReviewForm
          productId={productId}
          onSubmit={handleSubmitReview}
          onCancel={() => setShowReviewForm(false)}
        />
      )}

      <div className="reviews-list">
        {reviews.length === 0 ? (
          <div className="no-reviews">
            <p>No reviews yet. Be the first to review this product!</p>
          </div>
        ) : (
          reviews.map(review => (
            <ReviewItem
              key={review.id}
              review={review}
              onVote={handleVote}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ProductReviews;
